import type { ReactElement } from "react";
import { COLOR, type Tono } from "@/components/brand/elementos";
import { EN_CUADRADO, ISOTIPO, MARCA } from "@/components/ui/isotipo";

/**
 * La marca de La Tablapp: el **isotipo** (la flecha que avanza), sobre un cuadrado
 * redondeado. Es la misma pieza del favicon (`icon.svg`).
 *
 * Va el isotipo y no el lockup porque este componente firma pantallas donde el nombre
 * ya está escrito al lado (footers, 404, errores) o donde no hay ancho para leerlo.
 *
 * Colores **literales** (no CSS-vars) a propósito: así se ve correcto también fuera
 * del árbol con tema (p. ej. `global-error`, que renderiza su propio `<html>`).
 */
export function BrandMark({ size = 64, className = "" }: { size?: number; className?: string }): ReactElement {
  const { escala, x, y, lado } = EN_CUADRADO;
  return (
    <svg viewBox={`0 0 ${lado} ${lado}`} width={size} height={size} role="img" aria-label="La Tablapp" className={className}>
      <rect width={lado} height={lado} rx="14" fill={MARCA.fondo} />
      <g transform={`translate(${x} ${y}) scale(${escala})`}>
        {ISOTIPO.tinta.map((d) => (
          <path key={d.slice(0, 12)} d={d} fill={MARCA.tinta} />
        ))}
        <path d={ISOTIPO.verde} fill={MARCA.verde} />
      </g>
    </svg>
  );
}

/**
 * El isotipo **suelto**, sin su cuadrado: donde el nombre ya va escrito al lado (el
 * sidebar de la consola, el header de la vitrina) o donde no hay ancho para el lockup.
 *
 * A diferencia de `BrandMark`, este sí toma **tokens**: se pinta dentro del árbol con
 * tema, así que se re-tintea solo. `tono` lo fuerza a un color del kit para usos
 * decorativos. La geometría es la misma de `isotipo.ts`, así que las dos no pueden
 * divergir.
 *
 * La flecha va en `accent` —el verde de MARCA— y no en `win`, que es el semántico de
 * victoria (ADR-004 §2.2) y otro verde. Estuvo en `win` y nadie lo vio: los dos pasan
 * contraste igual de bien y el logo se lee como logo en cualquiera de los dos. Se corrigió
 * en FEATURE-102, junto con el mismo error en la landing. Ojo con el alcance: acá el
 * cambio se ve en las once superficies que montan el isotipo, no solo en la vitrina.
 */
export function Isotipo({ className = "h-6", tono }: { className?: string; tono?: Tono }): ReactElement {
  return (
    <svg aria-hidden viewBox={ISOTIPO.viewBox} className={`w-auto ${className}`}>
      {ISOTIPO.tinta.map((d) => (
        <path key={d.slice(0, 12)} d={d} fill={tono ? COLOR[tono] : COLOR.ink} />
      ))}
      <path d={ISOTIPO.verde} fill={tono ? COLOR[tono] : COLOR.accent} />
    </svg>
  );
}
